import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import Lesson from "./pages/Lesson";
import Practice from "./pages/Practice";
import Test from "./pages/Test";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import Profile from "./pages/Profile";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Settings from "./pages/Settings";
import Vocabulary from "./pages/Vocabulary";
import Conversations from "./pages/Conversations";
import Situations from "./pages/Situations";
import Phrases from "./pages/Phrases";
import Listening from "./pages/Listening";
import Reading from "./pages/Reading";
import Landing from "./pages/Landing";
import ConversationPractice from "./pages/ConversationPractice";
import Progress from "./pages/Progress";
import Onboarding from "./pages/Onboarding";
import MiniGames from "./pages/MiniGames";
import Stories from "./pages/Stories";
import Idioms from "./pages/Idioms";
import Bookmarks from "./pages/Bookmarks";
import PlacementTest from "./pages/PlacementTest";
import Shop from "./pages/Shop";
import Certificate from "./pages/Certificate";
import VoiceRecorder from "./pages/VoiceRecorder";
import ApiDocs from "./pages/ApiDocs";
import Pricing from "./pages/Pricing";
import Leaderboard from "./pages/Leaderboard";
import Topics from "./pages/Topics";
import SpeakPractice from "./pages/SpeakPractice";
import History from "./pages/History";
import OralLesson from "./pages/OralLesson";
import Calendar from "./pages/Calendar";
import Achievements from "./pages/Achievements";
import PronunciationGym from "./pages/PronunciationGym";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";
import SpeakHub from "./pages/hubs/Speak";
import WriteHub from "./pages/hubs/Write";
import PracticeHub from "./pages/hubs/Practice";
import CareerHub from "./pages/hubs/Career";
import PennyHub from "./pages/hubs/Penny";
import RephraseStudio from "./pages/write/RephraseStudio";
import EmailAssistant from "./pages/write/EmailAssistant";
import WhatsAppConverter from "./pages/write/WhatsAppConverter";
import IeltsWriting from "./pages/write/IeltsWriting";
import Translator from "./pages/write/Translator";
import InterviewPractice from "./pages/career/InterviewPractice";
import OfficeEnglish from "./pages/career/OfficeEnglish";
import PennyInsights from "./pages/penny/Insights";
import TemplateIndex from "./pages/templates/TemplateIndex";
import TemplatePage from "./pages/templates/TemplatePage";
import { saveAuth, clearAuth } from "./api";
import BottomNav from "./components/BottomNav";
import { NativeLanguageProvider } from "./contexts/NativeLanguageContext";
import LanguagePickerModal from "./components/LanguagePickerModal";
import AchievementToast from "./components/AchievementToast";
import StreakFreezeToast from "./components/StreakFreezeToast";

function App() {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("currentUser");
    return saved ? JSON.parse(saved) : null;
  });
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  function handleLogin(userData, token) {
    setUser(userData);
    if (token) {
      saveAuth(token, userData);
    } else {
      localStorage.setItem("currentUser", JSON.stringify(userData));
    }
  }

  function handleLogout() {
    setUser(null);
    clearAuth();
  }

  function handleUpdateUser(updatedUser) {
    setUser(updatedUser);
    localStorage.setItem("currentUser", JSON.stringify(updatedUser));
  }

  return (
    <NativeLanguageProvider>
    <Router>
      <LanguagePickerModal />
      <AchievementToast />
      <StreakFreezeToast />
      <Routes>
        {/* Public pages */}
        <Route path="/welcome" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/placement-test" element={<PlacementTest />} />
        <Route path="/api-docs" element={<ApiDocs />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />} />
        <Route path="/signup" element={user ? <Navigate to="/dashboard" /> : <Signup onLogin={handleLogin} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/templates" element={<TemplateIndex />} />
        <Route path="/templates/:slug" element={<TemplatePage />} />
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />

        {/* App pages — auth required */}
        <Route
          path="/*"
          element={
            !user ? (
              <AnonymousFallback />
            ) : (
              <div className={`min-h-screen transition-colors ${darkMode ? "bg-ink-900" : "bg-paper"}`}>
                <Navbar user={user} onLogout={handleLogout} darkMode={darkMode} setDarkMode={setDarkMode} />
                <BottomNav />
                <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-6">
                  <Routes>
                    <Route path="/dashboard" element={<Home />} />
                    {/* Hubs — outcome-oriented landing surfaces */}
                    <Route path="/speak"    element={<SpeakHub />} />
                    <Route path="/write"    element={<WriteHub />} />
                    <Route path="/write/rephrase" element={<RephraseStudio />} />
                    <Route path="/write/email"    element={<EmailAssistant />} />
                    <Route path="/write/whatsapp" element={<WhatsAppConverter />} />
                    <Route path="/write/ielts"    element={<IeltsWriting />} />
                    <Route path="/write/translate" element={<Translator />} />
                    <Route path="/practice" element={<PracticeHub />} />
                    <Route path="/career"   element={<CareerHub />} />
                    <Route path="/career/interview" element={<InterviewPractice />} />
                    <Route path="/career/office"    element={<OfficeEnglish />} />
                    <Route path="/penny"    element={<PennyHub />} />
                    <Route path="/penny/insights" element={<PennyInsights />} />
                    <Route path="/lesson/:day" element={<Lesson />} />
                    <Route path="/practice/:day" element={<Practice />} />
                    <Route path="/test/:day" element={<Test />} />
                    <Route path="/profile" element={<Profile user={user} onUpdateUser={handleUpdateUser} />} />
                    <Route path="/settings" element={<Settings user={user} darkMode={darkMode} setDarkMode={setDarkMode} onLogout={handleLogout} />} />
                    <Route path="/vocabulary" element={<Vocabulary />} />
                    <Route path="/conversations" element={<Conversations />} />
                    <Route path="/situations" element={<Situations />} />
                    <Route path="/phrases" element={<Phrases />} />
                    <Route path="/listening" element={<Listening />} />
                    <Route path="/reading" element={<Reading />} />
                    <Route path="/conversation-practice" element={<ConversationPractice />} />
                    <Route path="/progress" element={<Progress />} />
                    <Route path="/mini-games" element={<MiniGames />} />
                    <Route path="/stories" element={<Stories />} />
                    <Route path="/idioms" element={<Idioms />} />
                    <Route path="/bookmarks" element={<Bookmarks />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/certificate" element={<Certificate />} />
                    <Route path="/voice-recorder" element={<VoiceRecorder />} />
                    <Route path="/leaderboard" element={<Leaderboard />} />
                    <Route path="/scenarios" element={<Navigate to="/conversations" replace />} />
                    <Route path="/topics" element={<Topics />} />
                    <Route path="/speak-practice" element={<SpeakPractice />} />
                    <Route path="/history" element={<History />} />
                    <Route path="/oral/:day" element={<OralLesson />} />
                    <Route path="/calendar" element={<Calendar />} />
                    <Route path="/achievements" element={<Achievements />} />
                    <Route path="/pronunciation" element={<PronunciationGym />} />
                    <Route path="/admin" element={<Admin user={user} />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
              </div>
            )
          }
        />
      </Routes>
    </Router>
    </NativeLanguageProvider>
  );
}

// For anonymous users hitting /*: if they typed a real auth route, send
// them to /welcome to log in. If they typed garbage, show NotFound so
// 404s don't silently appear as the landing page.
const AUTH_ROUTES = new Set([
  "/dashboard", "/profile", "/settings", "/vocabulary", "/conversations",
  "/situations", "/phrases", "/listening", "/reading", "/conversation-practice",
  "/progress", "/mini-games", "/stories", "/idioms", "/bookmarks", "/shop",
  "/certificate", "/voice-recorder", "/leaderboard", "/scenarios", "/topics",
  "/speak-practice", "/history", "/calendar", "/achievements", "/pronunciation",
  "/admin",
  "/speak", "/write", "/practice", "/career", "/penny",
  "/write/rephrase", "/write/email", "/write/whatsapp", "/write/ielts", "/write/translate",
  "/career/interview", "/career/office",
  "/penny/insights",
]);
const AUTH_ROUTE_PREFIXES = ["/lesson/", "/practice/", "/test/", "/oral/"];

function AnonymousFallback() {
  const { pathname } = useLocation();
  const isKnownAuthRoute =
    AUTH_ROUTES.has(pathname) || AUTH_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
  return isKnownAuthRoute ? <Navigate to="/welcome" replace /> : <NotFound />;
}

export default App;
